import { z } from 'zod';
import { apiEnvelope } from '../envelope';

// Wire schemas for the guided-conversations introduction: the picker read
// (`GET /api/v1/home/guided-conversations`) and the shown marker write
// (`POST /api/v1/home/guided-conversations/shown`).
//
// The picker carries only what the member sees and the opaque id the existing
// practice launch takes. Which series or technique a session belongs to stays
// on the server, so nothing here can vary by programme or condition.

/** One welcome session the member can choose to start. */
export const guidedConversationSessionSchema = z.object({
  composedPracticeId: z.string().uuid(),
  title: z.string().min(1),
  hook: z.string().min(1),
  /** True for exactly one session: the one the plan puts first. */
  recommended: z.boolean(),
});
export type GuidedConversationSession = z.infer<typeof guidedConversationSessionSchema>;

/**
 * The picker payload. Deliberately not `.strict()`: the client parses it, and
 * a field added later must not fail an installed build (ADR-135 Rule 1).
 */
export const guidedConversationsDataSchema = z.object({
  sessions: z.array(guidedConversationSessionSchema),
});
export type GuidedConversationsData = z.infer<typeof guidedConversationsDataSchema>;

export const guidedConversationsResponseSchema = apiEnvelope(guidedConversationsDataSchema);
export type GuidedConversationsResponse = z.infer<typeof guidedConversationsResponseSchema>;

/**
 * The shown write takes no input: the member comes from the bearer token and
 * the instant from the server clock. Strict, so a client cannot smuggle a
 * member id or a timestamp into it.
 */
export const markGuidedConversationsShownRequestSchema = z.object({}).strict();
export type MarkGuidedConversationsShownRequest = z.infer<
  typeof markGuidedConversationsShownRequestSchema
>;

/**
 * The shown write's answer: the instant the member was FIRST shown the
 * introduction. A repeat returns the same instant, which is what makes a retry
 * indistinguishable from the first call. Non-strict, like every client-parsed
 * response.
 */
export const guidedConversationsShownDataSchema = z.object({
  shownAt: z.string().datetime({ offset: true }),
});
export type GuidedConversationsShownData = z.infer<typeof guidedConversationsShownDataSchema>;

export const guidedConversationsShownResponseSchema = apiEnvelope(
  guidedConversationsShownDataSchema,
);
export type GuidedConversationsShownResponse = z.infer<
  typeof guidedConversationsShownResponseSchema
>;
